import { Icons } from '@Components/icon/Icon';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { reportViewExpireToken } from '@Services/analytics/GeneralAnalaytics.tsx';
import { useRouteTracker } from '@Providers/RouteTrackerProvider/context.ts';
import { useBoundStore } from '@Store/useBoundStore';
import { ErrorModalTypes, IErrorHandling, OnboardingFormFields } from '@Types/index';
import { t } from 'i18next';
import { GenericMessageTypes } from '@Pages/genericMessagePage/queryParamsSchema';

export const AuthErrorMap = {
  TOKEN_EXPIRED: 401,
  ACCESS_DENIED: 40001,
  ACCESS_DENIED_IP_NOT_ALLOWED: 40002,
  INCORRECT_USER_PASSWORD: 40033,
  OKTA_USER_NOT_EXISTS: 40034,
  INCORRECT_USER_PASSWORD_BAD_REQUEST: 40035,
  MFA_FACTOR_ALREADY_EXISTS: 40030,
  MFA_ALREADY_ACTIVATE: 40036,
  MFA_NOT_ACTIVE: 40037,
  MFA_NOT_PENDING: 40038,
  MFA_REQUIRE: 4039,
  MFA_BAD_STATE_TOKEN: 40040,
  MFA_NOT_EXISTS: 40041,
  MFA_BAD_STATE_TOKEN_EXPIRE: 40042,
  MFA_CODE_IS_INVALID: 40032,
  JWT_TOKEN_EXPIRE: 40043,
  JWT_BAD_TOKEN: 40044,
  MFA_SECURITY_ANSWER_WEAK: 40045,
  MFA_INVALID_QUESTION: 40046,
  REFRESH_TOKEN_EXPIRE: 40047,
  BAD_CLIENT_ID: 40048,
  INCORRECT_USER_PASSWORD_FOR_BROWSER: 40049,
  MFA_BAD_TYPE: 40050,
  BAD_TOKEN: 40052,
  USER_LOCKED: 4026,
  CAPTCHA_FAILED: 4056,
  CUSTOMER_LEAD_NOT_FOUND: 4057,
  AUTHORIZED_USER_INVITE_CANCELED: 50722,
  AUTHORIZED_USER_ALREADY_REGISTER: 50724,
  AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING: 50725,
  MFA_CODE_ALREADY_USED: 40082,
};

const authErrorModalHandler = () => {
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
  if (isEmbedded) {
    const httpOrHttps = window.location.protocol;
    const hostname =
      window.location.hostname == 'localhost' ? 'localhost:8082' : window.location.hostname;
    const token = useBoundStore.getState().onboardingSlice.partnerToken;
    const url = `${httpOrHttps}//${hostname}/?token=${token}`;
    window.location.replace(url);
  }
};

export const AuthErrorsObjectMap = new Map<number, IErrorHandling>([
  [
    AuthErrorMap.CAPTCHA_FAILED,
    {
      errorCode: AuthErrorMap.USER_LOCKED,
      redirectPath: routesModel[RoutesMap.WELCOME].path,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.CUSTOMER_LEAD_NOT_FOUND,
    {
      errorCode: AuthErrorMap.USER_LOCKED,
      redirectPath: routesModel[RoutesMap.WELCOME].path,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.USER_LOCKED,
    {
      errorCode: AuthErrorMap.USER_LOCKED,
      redirectPath: routesModel[RoutesMap.WELCOME].path,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.TOKEN_EXPIRED,
    {
      errorCode: AuthErrorMap.TOKEN_EXPIRED,
      redirectPath: routesModel[RoutesMap.WELCOME].path,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {
        const { prevRoute } = useRouteTracker();
        reportViewExpireToken(prevRoute);
      },
    },
  ],

  [
    AuthErrorMap.INCORRECT_USER_PASSWORD,
    {
      errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.PASSWORD,
      callBack: () => {},
    },
  ],

  [
    AuthErrorMap.INCORRECT_USER_PASSWORD_BAD_REQUEST,
    {
      errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD_BAD_REQUEST,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.PASSWORD,
      callBack: () => {},
    },
  ],

  [
    AuthErrorMap.JWT_TOKEN_EXPIRE,
    {
      errorCode: AuthErrorMap.JWT_TOKEN_EXPIRE,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
      openModal: true,
      modalProps: {
        title: t('enErrors:auth.title'),
        content: t('enErrors:auth.description'),
        icon: Icons.Oops,
        buttonText: t('enErrors:auth.button'),
        type: ErrorModalTypes.GENERAL,
        onSubmit: () => {
          authErrorModalHandler();
        },
      },
    },
  ],
  [
    AuthErrorMap.JWT_BAD_TOKEN,
    {
      errorCode: AuthErrorMap.JWT_BAD_TOKEN,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
      openModal: true,
      modalProps: {
        title: t('enErrors:jwt.title'),
        content: t('enErrors:jwt.description'),
        icon: Icons.Oops,
        buttonText: t('enErrors:jwt.button'),
        type: ErrorModalTypes.GENERAL,
        onSubmit: () => {
          authErrorModalHandler();
        },
      },
    },
  ],

  [
    AuthErrorMap.INCORRECT_USER_PASSWORD_FOR_BROWSER,
    {
      errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD_FOR_BROWSER,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.PASSWORD,
      callBack: () => {},
    },
  ],

  [
    AuthErrorMap.BAD_TOKEN,
    {
      errorCode: AuthErrorMap.BAD_TOKEN,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
      openModal: true,
      modalProps: {
        title: t('enErrors:jwt.title'),
        content: t('enErrors:jwt.description'),
        icon: Icons.Oops,
        buttonText: t('enErrors:jwt.button'),
        type: ErrorModalTypes.GENERAL,
        onSubmit: () => {
          authErrorModalHandler();
        },
      },
    },
  ],
  [
    AuthErrorMap.AUTHORIZED_USER_INVITE_CANCELED,
    {
      errorCode: AuthErrorMap.AUTHORIZED_USER_INVITE_CANCELED,
      redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}/?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.AUTHORIZED_USER_ALREADY_REGISTER,
    {
      errorCode: AuthErrorMap.AUTHORIZED_USER_ALREADY_REGISTER,
      redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}/?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING,
    {
      errorCode: AuthErrorMap.AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING,
      redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}/?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
      relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
      callBack: () => {},
    },
  ],
  [
    AuthErrorMap.MFA_CODE_ALREADY_USED,
    {
      errorCode: AuthErrorMap.MFA_CODE_ALREADY_USED,
      redirectPath: '',
      relatedFieldName: OnboardingFormFields.PHONE_OTP,
      callBack: () => {},
    },
  ],
]);
